import { ApiResponseV2, QuoteResponse } from "@vertical-insure/shared";
import { useContext, useState, useEffect } from "react";
import { FormError } from "../ui/FormError";
import { useFormContext, Controller } from "react-hook-form";
import { PetSingleVidLayout } from "./PetSingleVidLayout";
import { PetMultiVidLayout } from "./PetMultiVidLayout";
import { ProductFormContext } from "@/lib/product-form-context";

function isQuoteResponse(obj: unknown): obj is QuoteResponse {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'quote_id' in obj &&
    typeof (obj as { quote_id?: unknown }).quote_id === 'string' &&
    'product' in obj &&
    typeof (obj as { product?: unknown }).product === 'object' &&
    (obj as { product?: unknown }).product !== null
  );
}

function getOptionsByVidId(quote: ApiResponseV2): Record<string, QuoteResponse> {
  const allOptions = quote.decisions
    ?.flatMap((decision) =>
      decision.options
        .filter((opt) => opt.status === "success" && isQuoteResponse(opt.data))
        .map((opt) => isQuoteResponse(opt.data) ? opt.data : undefined)
    ) ?? [];
  const byVidId: Record<string, QuoteResponse> = {};
  allOptions.forEach((opt) => {
    if (isQuoteResponse(opt) && opt.vid_id) {
      byVidId[opt.vid_id] = opt;
    }
  });
  return byVidId;
}

interface SingleOffer {
  type: 'single';
  vid: string;
}

interface MultiOffer {
  type: 'multi';
  vids: string[];
  heading: string;
  description: string;
}

type Offer = SingleOffer | MultiOffer;

const OFFER_CONFIG: Record<'complete' | 'essentials', Offer[]> = {
  complete: [
    {
      type: 'single',
      vid: 'PT-000004',
    },
    {
      type: 'multi',
      vids: ['PT-000003', 'PT-000005'],
      heading: 'Comprehensive Care',
      description:
        'From surprise vet visits to regular checkups, this option helps you stay ahead of illness, injury, and routine care. Ideal for pet owners who want to be prepared for the unexpected and the everyday.',
    },
  ],
  essentials: [
    {
      type: 'single',
      vid: 'PT-000005',
    },
    {
      type: 'single',
      vid: 'PT-000006',
    },
  ],
};

export const PetLayout = ({ quote }: { quote: ApiResponseV2 }) => {
  const form = useFormContext();
  const [tab, setTab] = useState<'complete' | 'essentials'>('complete');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const productFormContext = useContext(ProductFormContext);

  // Flat map and key by vid_id
  const optionsByVidId = getOptionsByVidId(quote);

  // Function to filter available offers for a given tab
  const getAvailableOffers = (tabName: 'complete' | 'essentials'): Offer[] => {
    const offers = OFFER_CONFIG[tabName];
    return offers.filter((offer) => {
      if (offer.type === 'single') {
        return optionsByVidId[offer.vid] !== undefined;
      } else if (offer.type === 'multi') {
        return offer.vids.every((vid) => optionsByVidId[vid] !== undefined);
      }
      return false;
    });
  };

  // Get available offers for each tab
  const availableCompleteOffers = getAvailableOffers('complete');
  const availableEssentialsOffers = getAvailableOffers('essentials');

  // Auto-switch tab if current tab has no offers but the other tab does
  useEffect(() => {
    if (tab === 'complete' && availableCompleteOffers.length === 0 && availableEssentialsOffers.length > 0) {
      setTab('essentials');
    } else if (tab === 'essentials' && availableEssentialsOffers.length === 0 && availableCompleteOffers.length > 0) {
      setTab('complete');
    }
  }, [tab, availableCompleteOffers.length, availableEssentialsOffers.length]);

  // Determine if we should show tab selection
  const shouldShowTabs = availableCompleteOffers.length > 0 && availableEssentialsOffers.length > 0;

  // Get current available offers
  const currentOffers = tab === 'complete' ? availableCompleteOffers : availableEssentialsOffers;

  // Check if any offers are available at all
  const hasAnyOffers = availableCompleteOffers.length > 0 || availableEssentialsOffers.length > 0;

  // Clear selected quotes when switching tabs
  const handleTabChange = (newTab: 'complete' | 'essentials', field: { onChange: (value: { accepted_quotes: string[]; [key: string]: unknown }) => void; value: { accepted_quotes: string[]; [key: string]: unknown } }) => {
    setTab(newTab);
    // Clear quotes using the same pattern as AcceptAction/DeclineAction
    field.onChange({
      ...field.value,
      accepted_quotes: []
    });
    form.trigger(`products[${productFormContext}]`);
  };

  // Set selection_required to false
  useEffect(() => {
    const products = form.getValues && typeof form.getValues === 'function' ? form.getValues('products') : [];
    if (Array.isArray(products) && typeof form.setValue === 'function') {
      products.forEach((product: unknown, index: number) => {
        if (typeof index === 'number' && !isNaN(index) && typeof product !== 'undefined') {
          form.setValue(`products[${index}].selection_required`, false);
        }
      }); 
    }
  }, [form]);

  // Get pet name from the first available option
  let petName = 'your pet';
  for (const option of Object.values(optionsByVidId)) {
    const petNameValue = option?.policy_attributes?.pet_name;
    if (typeof petNameValue === 'string') {
      petName = petNameValue;
      break;
    }
  }

  // If no offers are available at all, render empty
  if (!hasAnyOffers) {
    return (
      <Controller
        control={form.control}
        name={`products[${productFormContext}]`}
        render={() => {
          return (
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-primary mb-2 text-center">Recommended Health Plans for {petName}</h2>
              <div className="text-center text-gray-500 py-8">
                No health plans are currently available.
              </div>
              <FormError />
            </div>
          );
        }}
      />
    );
  }

  return (
    <Controller
      control={form.control}
      name={`products[${productFormContext}]`}
      render={({ field }) => {
        return (
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-primary mb-2 text-center">Recommended Health Plans for {petName}</h2>
            {/* Toggle at the top - only show if both tabs have offers */}
            {shouldShowTabs && (
              <div className="flex justify-center mb-6 mt-2">
                <div className="flex w-[320px] bg-gray-200 rounded-full p-1">
                  <button
                    className={`w-1/2 py-2 rounded-full font-semibold text-sm transition-all
                      ${tab === 'complete'
                        ? 'bg-primary text-white shadow'
                        : 'bg-transparent text-gray-700'}
                    `}
                    style={{ zIndex: tab === 'complete' ? 1 : 0, position: 'relative' }}
                    onClick={() => handleTabChange('complete', field)}
                    type="button"
                  >
                    Complete
                  </button>
                  <button
                    className={`w-1/2 py-2 rounded-full font-semibold text-sm transition-all
                      ${tab === 'essentials'
                        ? 'bg-primary text-white shadow'
                        : 'bg-transparent text-gray-700'}
                    `}
                    style={{ zIndex: tab === 'essentials' ? 1 : 0, position: 'relative' }}
                    onClick={() => handleTabChange('essentials', field)}
                    type="button"
                  >
                    Essentials Only
                  </button>
                </div>
              </div>
            )}
            {/* Render offers for the current tab */}
            {currentOffers.map((offer, idx) => {
              const badgeText = currentOffers.length === 1 ? "RECOMMENDED" : "POPULAR";
              
              if (offer.type === 'single') {
                const vid = offer.vid!;
                const option = optionsByVidId[vid];
                if (!option) return null;
                const expandedState = expanded[vid] || false;
                return (
                  <PetSingleVidLayout
                    key={vid}
                    option={option}
                    expanded={expandedState}
                    onToggleExpand={() => setExpanded((prev) => ({ ...prev, [String(vid)]: !expandedState }))}
                    badgeText={badgeText}
                    showBadge={idx === 0}
                  />
                );
              } else if (offer.type === 'multi' && Array.isArray(offer.vids)) {
                const { vids, heading, description } = offer;
                if (!vids || vids.length === 0) return null;
                const options = vids.map((vid) => optionsByVidId[vid]).filter(Boolean);
                if (options.length !== vids.length) return null;
                const expandedKey = vids.join('-');
                const expandedState = expanded[expandedKey] || false;
                return (
                  <PetMultiVidLayout
                    key={expandedKey}
                    options={options}
                    expanded={expandedState}
                    onToggleExpand={() => setExpanded((prev) => ({ ...prev, [String(expandedKey)]: !expandedState }))}
                    heading={heading}
                    description={description}
                    showBadge={idx === 0}
                    badgeText={badgeText}
                  />
                );
              }
              return null;
            })}
            <FormError />
            <div className="text-center text-xs mt-2">
              Accepting coverage constitutes my electronic signature. I confirm that I have read, understand, and agree to the{' '}
              <a 
                href="https://legal.verticalinsure.com/pets.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary underline hover:text-primary/80 whitespace-nowrap"
              > 
                insurance notices and disclosures
              </a>
              .
            </div>
          </div>
        );
      }}
    />
  );
};
